import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Ticket, 
  MessageCircle, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  User,
  Bot,
  RefreshCw,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

const AdminSupportPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [chats, setChats] = useState([]);
  const [activeTab, setActiveTab] = useState('tickets');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, chatsRes] = await Promise.all([
        axios.get(`${API}/support/admin/tickets`, { withCredentials: true }),
        axios.get(`${API}/support/admin/chats`, { withCredentials: true })
      ]);
      setTickets(ticketsRes.data.tickets || []);
      setChats(chatsRes.data.chats || []);
    } catch (error) {
      console.error('Error fetching support data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/support/admin/ticket/${ticketId}`, { withCredentials: true });
      setTicketDetails(response.data);
      setSelectedTicket(ticketId);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      toast.error('Failed to load ticket details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/support/admin/ticket/${ticketId}/status`, {
        status: newStatus
      }, { withCredentials: true });
      
      toast.success(`Ticket status updated to ${newStatus}`);
      
      setTickets(prev => prev.map(t => 
        t.ticket_id === ticketId ? { ...t, status: newStatus } : t
      ));
      
      if (ticketDetails?.ticket?.ticket_id === ticketId) {
        setTicketDetails(prev => ({
          ...prev,
          ticket: { ...prev.ticket, status: newStatus }
        }));
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
      toast.error('Failed to update ticket status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-amber-100 text-amber-800 border-amber-200',
      in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
      resolved: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-zinc-100 text-zinc-600 border-zinc-200'
    };
    const icons = {
      open: <AlertCircle className="w-3 h-3" />,
      in_progress: <Clock className="w-3 h-3" />,
      resolved: <CheckCircle className="w-3 h-3" />,
      closed: <CheckCircle className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.open}`}>
        {icons[status]}
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-amber-100 text-amber-800',
      low: 'bg-green-100 text-green-800',
      normal: 'bg-amber-100 text-amber-800'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[priority] || styles.medium}`}>
        {priority}
      </span>
    );
  };

  const filteredTickets = statusFilter === 'all' 
    ? tickets 
    : tickets.filter(t => t.status === statusFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header - Consistent with other pages */}
      <header className="px-6 py-3 bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-base font-bold text-zinc-900" style={{fontFamily: 'Outfit'}}>Support Center</h1>
            <p className="text-xs text-zinc-500">Manage customer support tickets</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-amber-600">{tickets.filter(t => t.status === 'open').length} Open</p>
              <p className="text-xs text-zinc-500">{tickets.length} Total Tickets</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchData}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={async () => {
                try {
                  await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
                  localStorage.removeItem('session_token');
                  navigate('/');
                } catch (err) {
                  localStorage.removeItem('session_token');
                  navigate('/');
                }
              }}
              className="border-zinc-300 text-zinc-700 hover:bg-zinc-50"
              data-testid="support-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content - Consistent width */}
      <main className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Tabs - Amber colors */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setActiveTab('tickets')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'tickets'
                  ? 'bg-amber-500 text-white'
                  : 'bg-white text-zinc-600 hover:bg-zinc-100'
              }`}
            >
              <Ticket className="w-4 h-4" />
              Tickets ({tickets.length})
            </button>
            <button
              onClick={() => setActiveTab('chats')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'chats'
                  ? 'bg-amber-500 text-white'
                  : 'bg-white text-zinc-600 hover:bg-zinc-100'
              }`}
            >
              <MessageCircle className="w-4 h-4" />
              All Chats ({chats.length})
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tickets/Chats List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      {activeTab === 'tickets' ? 'Support Tickets' : 'Chat Sessions'}
                    </CardTitle>
                    {activeTab === 'tickets' && (
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="text-xs border rounded px-2 py-1"
                      >
                        <option value="all">All Status</option>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[600px] overflow-y-auto overflow-x-hidden">
                  {activeTab === 'tickets' ? (
                    filteredTickets.length === 0 ? (
                      <p className="text-center text-zinc-500 py-8">No tickets found</p>
                    ) : (
                      filteredTickets.map(ticket => (
                        <div
                          key={ticket.ticket_id}
                          onClick={() => fetchTicketDetails(ticket.ticket_id)}
                          className={`p-3 rounded-lg cursor-pointer transition ${
                            selectedTicket === ticket.ticket_id
                              ? 'bg-amber-50 border border-amber-200'
                              : 'bg-zinc-50 hover:bg-zinc-100'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <span className="text-sm font-medium text-zinc-900 truncate flex-1 min-w-0">
                              {ticket.subject}
                            </span>
                            <span className="flex-shrink-0">{getPriorityBadge(ticket.priority)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-zinc-500 truncate flex-1 min-w-0">{ticket.user_email}</span>
                            <span className="flex-shrink-0">{getStatusBadge(ticket.status)}</span>
                          </div>
                          <div className="text-xs text-zinc-400 mt-1">
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))
                    )
                  ) : (
                    chats.length === 0 ? (
                      <p className="text-center text-zinc-500 py-8">No chats found</p>
                    ) : (
                      chats.map(chat => (
                        <div
                          key={chat.chat_id}
                          className="p-3 rounded-lg bg-zinc-50 hover:bg-zinc-100 cursor-pointer"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-zinc-900">
                              {chat.project_context?.business_name || 'Unknown Project'}
                            </span>
                            {chat.has_ticket && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                Has Ticket
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-zinc-500">{chat.user_email}</p>
                          <p className="text-xs text-zinc-400 mt-1">
                            {chat.messages?.length || 0} messages
                          </p>
                        </div>
                      ))
                    )
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Ticket Details */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    {selectedTicket ? 'Ticket Details' : 'Select a ticket'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingDetails ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                    </div>
                  ) : ticketDetails ? (
                    <div className="space-y-6">
                      {/* Ticket Info */}
                      <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-50 rounded-lg">
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Subject</p>
                          <p className="font-medium text-zinc-900">{ticketDetails.ticket.subject}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Customer</p>
                          <p className="font-medium text-zinc-900">{ticketDetails.ticket.user_email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Project</p>
                          <p className="font-medium text-zinc-900">{ticketDetails.ticket.project_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Priority</p>
                          {getPriorityBadge(ticketDetails.ticket.priority)}
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Created</p>
                          <p className="text-sm text-zinc-700">
                            {new Date(ticketDetails.ticket.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Status</p>
                          <select
                            value={ticketDetails.ticket.status}
                            onChange={(e) => updateTicketStatus(ticketDetails.ticket.ticket_id, e.target.value)}
                            disabled={updatingStatus}
                            className="text-sm border rounded px-2 py-1 bg-white"
                          >
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                        </div>
                      </div>

                      {/* Message Content for Direct Tickets */}
                      {ticketDetails.ticket.message && (
                        <div>
                          <h4 className="font-medium text-zinc-900 mb-3">Message</h4>
                          <div className="p-4 bg-white border border-zinc-200 rounded-lg">
                            <p className="text-sm text-zinc-700">{ticketDetails.ticket.message}</p>
                          </div>
                        </div>
                      )}

                      {/* Chat History */}
                      {ticketDetails.chat?.messages?.length > 0 && (
                        <div>
                          <h4 className="font-medium text-zinc-900 mb-3">Conversation History</h4>
                          <div className="space-y-3 max-h-[400px] overflow-y-auto p-4 bg-white border border-zinc-200 rounded-lg">
                            {ticketDetails.chat.messages.map((msg, idx) => (
                              <div key={idx} className="flex gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  msg.role === 'user' 
                                    ? 'bg-zinc-200' 
                                    : 'bg-gradient-to-br from-amber-500 to-yellow-500'
                                }`}>
                                  {msg.role === 'user' 
                                    ? <User className="w-4 h-4 text-zinc-600" />
                                    : <Bot className="w-4 h-4 text-white" />
                                  }
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-zinc-900">
                                      {msg.role === 'user' ? 'Customer' : 'AI Assistant'}
                                    </span>
                                    <span className="text-xs text-zinc-400">
                                      {new Date(msg.timestamp).toLocaleTimeString()}
                                    </span>
                                  </div>
                                  <p className="text-sm text-zinc-700 bg-zinc-50 p-3 rounded-lg">
                                    {msg.content}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-zinc-500">
                      <Ticket className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                      <p>Select a ticket from the list to view details</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSupportPage;
